import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Monitor,
  Tv,
  Laptop,
  MoreVertical,
  Trash2,
  Settings,
  ExternalLink,
  Wifi,
  WifiOff,
  ListVideo,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { AssignPlaylistDialog } from './AssignPlaylistDialog';

interface Display {
  id: string;
  name: string;
  description: string | null;
  device_type: string | null;
  is_active: boolean;
  last_heartbeat_at: string | null;
  orientation: string;
  paired_at: string | null;
  created_at: string;
  business_id: string;
  default_playlist?: {
    id: string;
    name: string;
  } | null;
}

interface DisplayCardProps {
  display: Display;
  isOnline: boolean;
  onDelete: () => void;
  onToggleActive: (active: boolean) => void;
  onRefresh: () => void;
}

export function DisplayCard({
  display,
  isOnline,
  onDelete,
  onToggleActive,
  onRefresh,
}: DisplayCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  const getDeviceIcon = () => {
    switch (display.device_type) {
      case 'smart_tv':
        return <Tv className="w-5 h-5" />;
      case 'android_box':
      case 'fire_stick':
      case 'chromecast':
        return <Monitor className="w-5 h-5" />;
      case 'browser':
        return <Laptop className="w-5 h-5" />;
      default:
        return <Monitor className="w-5 h-5" />;
    }
  };

  const getDeviceLabel = () => {
    switch (display.device_type) {
      case 'smart_tv':
        return 'Smart TV';
      case 'android_box':
        return 'Android Box';
      case 'fire_stick':
        return 'Fire TV';
      case 'chromecast':
        return 'Chromecast';
      case 'browser':
        return 'Browser';
      case 'raspberry_pi':
        return 'Raspberry Pi';
      default:
        return 'Display';
    }
  };

  const getLastSeenText = () => {
    if (!display.last_heartbeat_at) return 'Never connected';
    return `Last seen ${formatDistanceToNow(new Date(display.last_heartbeat_at), { addSuffix: true })}`;
  };

  return (
    <>
      <Card className={!display.is_active ? 'opacity-60' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isOnline ? 'bg-green-500/10' : 'bg-muted'}`}>
                {getDeviceIcon()}
              </div>
              <div>
                <CardTitle className="text-base">{display.name}</CardTitle>
                <CardDescription className="text-xs">
                  {getDeviceLabel()}
                  {display.orientation === 'portrait' && ' • Portrait'}
                </CardDescription>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowAssignDialog(true)}>
                  <ListVideo className="w-4 h-4 mr-2" />
                  Assign Playlist
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Display
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Display
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOnline ? (
                <>
                  <Wifi className="w-4 h-4 text-green-500" />
                  <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-200">
                    Online
                  </Badge>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-muted-foreground" />
                  <Badge variant="outline" className="text-muted-foreground">
                    Offline
                  </Badge>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Active</span>
              <Switch
                checked={display.is_active}
                onCheckedChange={onToggleActive}
              />
            </div>
          </div>

          {/* Playlist */}
          <div
            className="flex items-center gap-2 p-2 rounded-md bg-muted/50 cursor-pointer hover:bg-muted"
            onClick={() => setShowAssignDialog(true)}
          >
            <ListVideo className="w-4 h-4 text-muted-foreground" />
            {display.default_playlist ? (
              <span className="text-sm">{display.default_playlist.name}</span>
            ) : (
              <span className="text-sm text-muted-foreground">No playlist assigned</span>
            )}
          </div>

          {/* Last seen */}
          <p className="text-xs text-muted-foreground">{getLastSeenText()}</p>

          {/* Description */}
          {display.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {display.description}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Display</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{display.name}"? The display will need to be paired again to reconnect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Playlist Dialog */}
      <AssignPlaylistDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        displayId={display.id}
        displayName={display.name}
        businessId={display.business_id}
        currentPlaylistId={display.default_playlist?.id || null}
        onAssigned={onRefresh}
      />
    </>
  );
}
